/**
 * The theme contract, in one place the Theme Builder and the contrast
 * checker can both read. theme.css is still the source of the VALUES;
 * this file is the list of which tokens a theme (or a user override) may set.
 */

export const COLOR_TOKENS = [
  { id: '--bg-default',     label: 'Page',           group: 'surfaces' as const },
  { id: '--bg-surface',     label: 'Card',           group: 'surfaces' as const },
  { id: '--bg-surface-alt', label: 'Recessed',       group: 'surfaces' as const },
  { id: '--text-primary',   label: 'Body text',      group: 'ink' as const },
  { id: '--text-secondary', label: 'Supporting',     group: 'ink' as const },
  { id: '--text-tertiary',  label: 'Meta labels',    group: 'ink' as const },
  { id: '--border-soft',    label: 'Hairline',       group: 'edges' as const },
  { id: '--border-strong',  label: 'Divider',        group: 'edges' as const },
  { id: '--brand-primary',  label: 'Accent',         group: 'brand' as const },
  { id: '--semantic-success', label: 'Success',      group: 'status' as const },
  { id: '--semantic-warning', label: 'Warning',      group: 'status' as const },
  { id: '--semantic-error',   label: 'Error',        group: 'status' as const },
] as const

export const GEOMETRY_TOKENS = [
  { id: '--rail-width',    label: 'Rail width',   min: 12,  max: 24,   step: 0.5, unit: 'rem' as const },
  { id: '--header-height', label: 'Header',       min: 2.5, max: 6,    step: 0.25, unit: 'rem' as const },
  { id: '--page-pad',      label: 'Page padding', min: 0,   max: 2,    step: 0.25, unit: 'rem' as const },
  { id: '--card-pad',      label: 'Card padding', min: 0.5, max: 2,    step: 0.25, unit: 'rem' as const },
] as const

export type ColorTokenId = typeof COLOR_TOKENS[number]['id']
export type GeometryTokenId = typeof GEOMETRY_TOKENS[number]['id']
export type UserThemeOverride = Partial<Record<ColorTokenId | GeometryTokenId, string>>

export const USER_OVERRIDE_KEY = 'horton-user-override'
export const USER_OVERRIDE_STYLE_ID = 'user-theme-override'

const COLOR_OK = /^(#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})|rgba?\(|hsla?\(|color-mix\(|var\(--)/i

export function isSafeColor(value: string): boolean {
  const v = value.trim()
  return v.length > 0 && v.length < 160 && COLOR_OK.test(v) && !/[;{}]/.test(v)
}

export function isSafeLength(value: string): boolean {
  return /^\d+(\.\d+)?rem$/.test(value.trim())
}

export function parseOverride(raw: string | null): UserThemeOverride {
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw) as UserThemeOverride
    const out: UserThemeOverride = {}
    for (const t of COLOR_TOKENS) {
      const v = parsed[t.id]
      if (typeof v === 'string' && isSafeColor(v)) out[t.id] = v.trim()
    }
    for (const t of GEOMETRY_TOKENS) {
      const v = parsed[t.id]
      if (typeof v === 'string' && isSafeLength(v)) out[t.id] = v.trim()
    }
    return out
  } catch {
    return {}
  }
}

export function applyUserOverride(override: UserThemeOverride): void {
  const existing = document.getElementById(USER_OVERRIDE_STYLE_ID)
  const entries = Object.entries(override).filter(([, v]) => v)
  if (entries.length === 0) {
    existing?.remove()
    return
  }
  const body = entries.map(([k, v]) => `  ${k}: ${v};`).join('\n')
  const css = `:root {\n${body}\n}`
  const el = existing ?? Object.assign(document.createElement('style'), { id: USER_OVERRIDE_STYLE_ID })
  el.textContent = css
  if (!existing) document.head.appendChild(el)
}

/** sRGB relative luminance, WCAG. Accepts a canvas-rasterised [r,g,b]. */
export function luminance([r, g, b]: number[]): number {
  const f = (v: number) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b)
}

export function contrastRatio(a: number[], b: number[]): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x)
  return (hi + 0.05) / (lo + 0.05)
}

export function rasterise(color: string, over?: string): number[] {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = 1
  const ctx = canvas.getContext('2d')
  if (!ctx) return [0, 0, 0]
  if (over) {
    ctx.fillStyle = over
    ctx.fillRect(0, 0, 1, 1)
  }
  ctx.fillStyle = color
  ctx.fillRect(0, 0, 1, 1)
  const d = ctx.getImageData(0, 0, 1, 1).data
  return [d[0], d[1], d[2]]
}
